import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SiteMapCanvas from '../../components/SiteMapCanvas';
import GatesTable from '../../components/GatesTable';
import GateConfigMenu from '../../components/GateConfigMenu';
import GateActionsMenu from '../../components/GateActionsMenu';
import GateTimeline from '../../components/GateTimeline';
import GateControlPanel from '../../components/GateControlPanel';
import GateCommandsLog from '../../components/GateCommandsLog';
import GateTempUnlockSection from '../../components/GateTempUnlockSection';
import FaultHistoryBadge from '../../components/FaultHistoryBadge';
import FaultAcknowledgeButton from '../../components/FaultAcknowledgeButton';
import MqttStatusIndicator from '../../components/MqttStatusIndicator';
import HermeticLockIndicator from '../../components/HermeticLockIndicator';
import LevelerStatusIndicator from '../../components/LevelerStatusIndicator';
import AssignToGroupDialog from '../../components/AssignToGroupDialog';
import BulkCommandModal from '../../components/BulkCommandModal';
import WifiPrepCard from '../../components/WifiPrepCard';
import useIsMobile from '../../hooks/useIsMobile';
import usePinnedGates from '../../hooks/usePinnedGates';
import GatesListFilters, { filterGates } from '../../components/GatesListFilters';
import { SkeletonStatsRow, SkeletonMap, SkeletonTable, SkeletonBox } from '../../components/Skeleton';
import CustomerBreadcrumb from '../../components/CustomerBreadcrumb';
import LiveUpdatedAt from '../../components/LiveUpdatedAt';

const LIVE_POLL_INTERVAL_MS = 30000;

function CustomerSiteMap({ user }) {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [selectedGate, setSelectedGate] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGateIds, setSelectedGateIds] = useState([]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [bulkConfigOpen, setBulkConfigOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkCommandType, setBulkCommandType] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showGateModal, setShowGateModal] = useState(false);
  // Epoch-ms of the last successful live snapshot. Feeds the
  // "עודכן לפני X" indicator next to the page title so the user can
  // tell whether the dots they see are stale (e.g. network dropped).
  const [liveUpdatedAt, setLiveUpdatedAt] = useState(null);
  const liveTimer = useRef(null);
  // Mobile-only: switch between the gates list and the map. On
  // desktop both are shown together (room for both, and managers
  // want the overview). Default to the list because that's where
  // the per-gate actions live; users go to "map" when they need
  // spatial context. Edit mode forces map view (you edit the map).
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('list');
  // Search + status filter live above the gates list on mobile.
  // Map view ignores them — when looking at the floor plan we want
  // to see every gate so spatial context is preserved.
  const [listQuery, setListQuery] = useState('');
  const [listStatus, setListStatus] = useState('all');
  // Per-user, per-site favourites: pinned gates float to the top of
  // the mobile list. Stored in localStorage; see usePinnedGates.
  const { isPinned, togglePin, sortPinnedFirst } = usePinnedGates(id, user?.id);

  // Only a manager (the customer-owner) may reshape the site map. The
  // backend enforces the same flag independently — this just hides the
  // controls from viewers.
  const canManageSite = !!user?.permissions?.can_manage_sites_and_groups;

  const loadSite = () => {
    axios.get(`/api/customer/sites/${id}`).then(res => setSite(res.data));
  };

  useEffect(() => {
    axios.get(`/api/customer/sites/${id}`).then(res => {
      setSite(res.data);
      setLiveUpdatedAt(Date.now());
    });
  }, [id]);

  // Poll only the live-changing fields so the map reflects open/close
  // events within ~30s without re-downloading positions and product
  // data on every tick. Paused while editing the layout — a /live
  // response landing mid-drag would be jarring (mirrors the admin map).
  const siteLoaded = site !== null;
  useEffect(() => {
    if (!siteLoaded || editMode) return undefined;
    const poll = () => {
      axios.get(`/api/customer/sites/${id}/live`).then(res => {
        const liveById = new Map(res.data.gates.map(g => [g.id, g]));
        setSite(prev => prev && ({
          ...prev,
          gates: prev.gates.map(g => {
            const live = liveById.get(g.id);
            return live ? { ...g, ...live } : g;
          }),
        }));
        setLiveUpdatedAt(Date.now());
      }).catch(() => {});
    };
    liveTimer.current = setInterval(poll, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(liveTimer.current);
  }, [id, siteLoaded, editMode]);

  if (!site) {
    // Skeleton placeholder matches the eventual layout (stats → map → table)
    // so the wait feels intentional and content lands without a layout shift.
    return (
      <>
        <div style={{ marginBottom: '1rem' }}>
          <SkeletonStatsRow cards={3} />
        </div>
        <SkeletonMap height={420} />
        <SkeletonTable rows={4} columns={5} />
      </>
    );
  }

  const totalGates = site.gates.length;
  // not_started gates have no live state yet — neither open nor closed —
  // so keep them out of both counters (they'd otherwise inflate "closed").
  const openGates = site.gates.filter(g => g.installation_status !== 'not_started' && g.is_open).length;
  const closedGates = site.gates.filter(g => g.installation_status !== 'not_started' && !g.is_open).length;

  const clearSelection = () => {
    setSelectionMode(false);
    setSelectedGateIds([]);
  };

  const toggleSelection = (gateId) => {
    setSelectedGateIds(prev =>
      prev.includes(gateId) ? prev.filter(x => x !== gateId) : [...prev, gateId]
    );
  };

  const handleGateClick = (gate) => {
    if (selectionMode) {
      toggleSelection(gate.id);
    } else {
      setSelectedGate(gate);
    }
  };

  // --- Map editing (manager only) ---
  const handleLayoutChange = async (layout) => {
    await axios.put(`/api/customer/sites/${id}/layout`, { layout });
    setSite(prev => prev && ({ ...prev, layout }));
  };

  const handleGateMove = async (gateId, x, y) => {
    await axios.put(`/api/customer/gates/${gateId}/position`, { position_x: x, position_y: y });
    setSite(prev => prev && ({
      ...prev,
      gates: prev.gates.map(g => g.id === gateId ? { ...g, position_x: x, position_y: y } : g),
    }));
  };

  const handleGateResize = async (gateId, scale) => {
    await axios.put(`/api/customer/gates/${gateId}/position`, { gate_scale: scale });
    setSite(prev => prev && ({
      ...prev,
      gates: prev.gates.map(g => g.id === gateId ? { ...g, gate_scale: scale } : g),
    }));
  };

  const handleGateDelete = async (gate) => {
    if (!window.confirm(`למחוק את השער "${gate.name}"? אפשר למחוק רק שער שעדיין לא הותקן.`)) return;
    try {
      await axios.delete(`/api/customer/gates/${gate.id}`);
      loadSite();
    } catch (err) {
      alert(err.response?.data?.error || 'מחיקה נכשלה');
    }
  };

  const hasSelection = selectedGateIds.length > 0 && !editMode;
  const canIssueCommands = !!user?.permissions?.can_open_close_gates;

  // Refresh the live snapshot so the map dots and the row status flip
  // the moment a per-card command acks. Without this the user would
  // wait up to LIVE_POLL_INTERVAL_MS (30s) to see their own click.
  const refreshLiveAfterCommand = () => {
    axios.get(`/api/customer/sites/${id}/live`).then(res => {
      const liveById = new Map(res.data.gates.map(g => [g.id, g]));
      setSite(prev => prev && ({
        ...prev,
        gates: prev.gates.map(g => {
          const live = liveById.get(g.id);
          return live ? { ...g, ...live } : g;
        }),
      }));
    }).catch(() => {});
  };

  return (
    <>
      <CustomerBreadcrumb items={[{ label: site.name }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>{site.name}</h2>
        {/* Live-data freshness indicator — lets the user see at a glance
            whether the dots on the map reflect a current snapshot. */}
        <LiveUpdatedAt timestamp={liveUpdatedAt} />
      </div>

      <WifiPrepCard
        siteId={id}
        initialDoneAt={site.wifi_prep_done_at}
        canManage={canManageSite}
        onChange={(doneAt) => setSite(prev => prev && ({ ...prev, wifi_prep_done_at: doneAt }))}
      />

      {/* Stat cards double as a quick filter for the gates list/table
          below. They write the same `listStatus` that the mobile filter
          chips use, so picking "פתוחים" here reflects on both surfaces.
          Clicking the active card again clears back to "all". */}
      <div className="stats-row">
        <StatFilterCard
          label="שערים"
          number={totalGates}
          active={listStatus === 'all'}
          onClick={() => setListStatus('all')}
        />
        <StatFilterCard
          label="סגורים"
          number={closedGates}
          tone="green"
          active={listStatus === 'closed'}
          onClick={() => setListStatus(listStatus === 'closed' ? 'all' : 'closed')}
        />
        <StatFilterCard
          label="פתוחים"
          number={openGates}
          tone="red"
          active={listStatus === 'open'}
          onClick={() => setListStatus(listStatus === 'open' ? 'all' : 'open')}
        />
      </div>

      {/* Phase 5.G — mobile view toggle. Edge-to-edge so it reads as
          the primary navigation control on the page. "קבוצות" used to
          sit beside it as a chip; PR-7 moves it into the sidebar
          (hamburger menu) so the toggle can be full width again. */}
      {isMobile && !editMode && (
        <div className="biz-view-toggle" role="tablist" aria-label="תצוגה">
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === 'list'}
            className={mobileView === 'list' ? 'is-active' : ''}
            onClick={() => setMobileView('list')}
          >
            רשימה
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === 'map'}
            className={mobileView === 'map' ? 'is-active' : ''}
            onClick={() => setMobileView('map')}
          >
            מפה
          </button>
        </div>
      )}

      {/* Map-related controls. PR-5: hide entirely while the mobile
          list is showing — these only make sense over the canvas. */}
      {(!isMobile || editMode || mobileView === 'map') && (
        <div className="biz-action-row" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          {!editMode && (
            <button
              data-active={selectionMode ? 'true' : 'false'}
              onClick={() => (selectionMode ? clearSelection() : setSelectionMode(true))}
              style={{
                padding: '0.4rem 0.85rem',
                border: '1px solid #d1d5db',
                background: selectionMode ? '#1f2937' : '#fff',
                color: selectionMode ? '#fff' : '#374151',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
              title="במצב בחירה, לחיצה על שער במפה מסמנת אותו במקום לפתוח את הכרטיס"
            >
              {selectionMode ? 'בטל בחירה במפה' : 'בחירה במפה'}
            </button>
          )}
          {editMode && (
            <button
              onClick={() => setShowGateModal(true)}
              style={{
                padding: '0.4rem 0.85rem',
                border: '1px solid #1f2937',
                background: '#1f2937',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              + שער חדש
            </button>
          )}
          {canManageSite && (
            <button
              data-active={editMode ? 'true' : 'false'}
              onClick={() => { setEditMode(!editMode); clearSelection(); }}
              style={{
                padding: '0.4rem 0.85rem',
                border: `1px solid ${editMode ? '#1f2937' : '#d1d5db'}`,
                background: editMode ? '#1f2937' : '#fff',
                color: editMode ? '#fff' : '#374151',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              {editMode ? 'סיום עריכה' : 'עריכת מפה'}
            </button>
          )}
        </div>
      )}

      {(!isMobile || editMode || mobileView === 'map') && (
        <SiteMapCanvas
          gates={site.gates}
          layout={site.layout}
          editable={editMode}
          onLayoutChange={handleLayoutChange}
          onGateMove={handleGateMove}
          onGateResize={handleGateResize}
          onGateClick={handleGateClick}
          onGateDelete={handleGateDelete}
          gateDeletable={(g) => g.installation_status === 'not_started'}
          selectionMode={!editMode && selectionMode}
          selectedGateIds={editMode ? [] : selectedGateIds}
          showLiveTimer={!editMode}
        />
      )}

      {(!isMobile || (mobileView === 'list' && !editMode)) && (
        <div style={{ marginTop: isMobile ? '0' : '1.25rem' }}>
          {isMobile && !editMode && (
            <GatesListFilters
              gates={site.gates}
              query={listQuery}
              onQueryChange={setListQuery}
              status={listStatus}
              onStatusChange={setListStatus}
            />
          )}
          {/* Desktop respects the status filter the user picked from
              the stat cards above (listStatus). Mobile also respects
              the search query from the chips strip. Edit mode shows
              every gate so the technician sees the full layout. */}
          {!isMobile && listStatus !== 'all' && !editMode && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem',
              fontSize: '0.78rem',
              color: '#1a1a2e',
            }}>
              <span style={{
                background: '#eef2ff',
                border: '1px solid #c7d2fe',
                padding: '0.25rem 0.65rem',
                borderRadius: 999,
              }}>
                מסונן: {listStatus === 'open' ? 'פתוחים בלבד' : listStatus === 'closed' ? 'סגורים בלבד' : 'לא מוגדרים'}
              </span>
              <button
                type="button"
                onClick={() => setListStatus('all')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1a1a2e',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontFamily: 'Heebo, sans-serif',
                }}
              >נקה סינון</button>
            </div>
          )}
          <GatesTable
            gates={editMode
              ? site.gates
              : isMobile
                ? sortPinnedFirst(filterGates(site.gates, { query: listQuery, status: listStatus }))
                : filterGates(site.gates, { query: '', status: listStatus })}
            selectedIds={editMode ? [] : selectedGateIds}
            onToggleSelect={editMode ? null : toggleSelection}
            onRowClick={editMode ? null : (gate) => setSelectedGate(gate)}
            canIssueCommands={canIssueCommands && !editMode}
            onCommandComplete={refreshLiveAfterCommand}
            isPinned={isMobile && !editMode ? isPinned : null}
            onTogglePin={isMobile && !editMode ? togglePin : null}
            extraHeaderCells={editMode ? <th style={{ width: '1%' }}>פעולות</th> : null}
            renderExtraCells={editMode ? (g) => (
              <td onClick={e => e.stopPropagation()}>
                <button className="btn btn-danger btn-sm" onClick={() => handleGateDelete(g)}>מחק</button>
              </td>
            ) : null}
          />
        </div>
      )}

      {/* Bulk-actions bar — appears any time at least one gate is
          selected, whether the selection happened from the map (in
          selection mode) or from the table checkboxes. The same
          actions menu serves both surfaces. */}
      {hasSelection && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #e5e7eb',
          padding: '0.85rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.85rem',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
          zIndex: 30,
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {selectedGateIds.length} נבחרו
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => setActionsMenuOpen(true)}
            style={{
              padding: '0.45rem 0.9rem',
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              borderRadius: '6px',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            פעולות
          </button>
          <button
            onClick={clearSelection}
            style={{
              padding: '0.45rem 0.9rem',
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            ביטול
          </button>
        </div>
      )}

      {actionsMenuOpen && (
        <GateActionsMenu
          gateIds={selectedGateIds}
          onClose={() => setActionsMenuOpen(false)}
          onPickSettings={() => {
            setActionsMenuOpen(false);
            setBulkConfigOpen(true);
          }}
          onPickAssignGroup={() => {
            setActionsMenuOpen(false);
            setBulkAssignOpen(true);
          }}
          onPickOpen={canIssueCommands ? () => {
            setActionsMenuOpen(false);
            setBulkCommandType('open');
          } : undefined}
          onPickClose={canIssueCommands ? () => {
            setActionsMenuOpen(false);
            setBulkCommandType('close');
          } : undefined}
        />
      )}

      {bulkCommandType && (
        <BulkCommandModal
          gateIds={selectedGateIds}
          gates={site.gates}
          commandType={bulkCommandType}
          apiBase="customer"
          onClose={() => setBulkCommandType(null)}
          onCompleted={() => {
            // Refresh live state so the row badges reflect the new
            // commands; keep the modal open so the user can read the
            // per-gate results until they dismiss it.
            axios.get(`/api/customer/sites/${id}/live`).then(res => {
              const liveById = new Map(res.data.gates.map(g => [g.id, g]));
              setSite(prev => prev && ({
                ...prev,
                gates: prev.gates.map(g => {
                  const live = liveById.get(g.id);
                  return live ? { ...g, ...live } : g;
                }),
              }));
            }).catch(() => {});
          }}
        />
      )}

      {bulkConfigOpen && (
        <GateConfigMenu
          gateIds={selectedGateIds}
          initialSettings={null}
          onClose={() => setBulkConfigOpen(false)}
          onApplied={() => {
            setBulkConfigOpen(false);
            axios.get(`/api/customer/sites/${id}`).then(res => setSite(res.data));
            clearSelection();
          }}
        />
      )}

      {bulkAssignOpen && (
        <AssignToGroupDialog
          siteId={id}
          gateIds={selectedGateIds}
          apiBase="customer"
          onClose={() => setBulkAssignOpen(false)}
          onApplied={() => {
            setBulkAssignOpen(false);
            axios.get(`/api/customer/sites/${id}`).then(res => setSite(res.data));
            clearSelection();
          }}
        />
      )}

      {showGateModal && (
        <NewGateModal
          siteId={id}
          onClose={() => setShowGateModal(false)}
          onCreated={() => { setShowGateModal(false); loadSite(); }}
        />
      )}

      {/* Per-gate detail surface. Two shells, one component:
         - Mobile: full-height slide-over from the side (existing).
         - Desktop: centred modal (room to breathe; doesn't crowd the
           map next to it the way a 360px column does).
         Both render the same <GatePanel/> inside. */}
      {selectedGate && (isMobile ? (
        <>
          <div className="slide-panel open">
            <GatePanel
              gate={
                // Re-resolve from the latest site so the panel sees the
                // freshest has_fault / is_offline / etc. on every poll.
                site.gates.find(g => g.id === selectedGate.id) || selectedGate
              }
              user={user}
              onClose={() => setSelectedGate(null)}
              showCloseButton={true}
            />
          </div>
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 40 }}
            onClick={() => setSelectedGate(null)}
          />
        </>
      ) : (
        <div
          className="gate-detail-modal-overlay"
          onClick={() => setSelectedGate(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="gate-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gate-detail-modal-close"
              onClick={() => setSelectedGate(null)}
              aria-label="סגור"
            >
              ✕
            </button>
            <GatePanel
              gate={
                site.gates.find(g => g.id === selectedGate.id) || selectedGate
              }
              user={user}
              onClose={() => setSelectedGate(null)}
              showCloseButton={false}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function GatePanel({ gate, user, onClose, showCloseButton = true }) {
  const [details, setDetails] = useState(null);
  const [settings, setSettings] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [commandsRefreshKey, setCommandsRefreshKey] = useState(0);
  // Phase 5.G PR-4 — tab navigation inside the slide panel.
  // Before this, the panel was one long scroll: status → controls →
  // settings → event history (infinite scroll) → commands. Users on
  // mobile had to scroll past 50+ events just to see "פקודות". Now
  // they're tabs at the top. Default is "סקירה" — the action
  // surface. Reset to overview when switching gates so each panel
  // open shows the primary view.
  const [tab, setTab] = useState('overview');
  useEffect(() => { setTab('overview'); }, [gate.id]);

  // PR 2 of the user-profile redesign: replaced the binary role
  // check with explicit permission flags so non-primary templates
  // (e.g. מנהל אחזקה — has no open/close) get the right surface.
  // Backend enforces independently on every endpoint.
  const canIssueCommands = !!user?.permissions?.can_open_close_gates;
  const canTempUnlock = !!user?.permissions?.can_temp_unlock;

  useEffect(() => {
    axios.get(`/api/customer/gates/${gate.id}`).then(res => setDetails(res.data));
    axios.get(`/api/customer/gates/${gate.id}/settings`).then(res => setSettings(res.data));
  }, [gate.id]);

  const refreshSettings = () => {
    axios.get(`/api/customer/gates/${gate.id}/settings`).then(res => setSettings(res.data));
  };

  const revertToGroup = () => {
    if (!window.confirm('להחזיר את השער להגדרות הקבוצה? ההגדרות הפרטיות שלו יימחקו.')) return;
    Promise.all([
      axios.delete(`/api/customer/gates/${gate.id}/settings`),
      axios.delete(`/api/customer/gates/${gate.id}/schedule`),
    ]).then(refreshSettings);
  };

  const inheritedFromGroup = settings?.source === 'group';
  const hasOwnOverride = settings?.source === 'gate';

  return (
    <>
      {showCloseButton && (
        <button className="slide-panel-close" onClick={onClose}>&#x2715;</button>
      )}
      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.4rem' }}>{gate.name}</h2>
      {gate.group_name && (
        <div style={{
          display: 'inline-block',
          fontSize: '0.78rem',
          color: '#1e40af',
          background: '#eff6ff',
          padding: '0.2rem 0.55rem',
          borderRadius: '4px',
          marginBottom: '1rem',
        }}>
          📁 קבוצה: {gate.group_name}
        </div>
      )}

      <div className="gate-panel-tabs" role="tablist" aria-label="תצוגה בכרטיס שער">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          className={tab === 'overview' ? 'is-active' : ''}
          onClick={() => setTab('overview')}
        >
          סקירה
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'events'}
          className={tab === 'events' ? 'is-active' : ''}
          onClick={() => setTab('events')}
        >
          אירועים
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'commands'}
          className={tab === 'commands' ? 'is-active' : ''}
          onClick={() => setTab('commands')}
        >
          פקודות
        </button>
      </div>

      {tab === 'overview' && (
      <>
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{
          padding: '0.85rem',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          textAlign: 'center',
          marginBottom: '0.75rem',
        }}>
          {gate.installation_status === 'not_started' ? (
            <>
              <span className="status-dot" style={{ marginLeft: '0.4rem' }}></span>
              <span style={{ fontWeight: 500 }}>לא מוגדר</span>
            </>
          ) : (
            <>
              <span className={`status-dot ${gate.is_open ? 'red' : 'green'}`} style={{ marginLeft: '0.4rem' }}></span>
              <span style={{ fontWeight: 500 }}>{gate.is_open ? 'פתוח' : 'סגור'}</span>
            </>
          )}
        </div>

        {details && (
          <HermeticLockIndicator
            wiringType={details.wiring_type}
            isLocked={!!details.is_hermetically_locked}
            shouldBeLocked={!!details.should_be_hermetically_locked}
          />
        )}

        {details && (
          <LevelerStatusIndicator
            isLevelerGate={!!details.is_continuous_with_leveler}
            levelerState={details.leveler_state}
            levelerStateChangedAt={details.leveler_state_changed_at}
          />
        )}

        {canIssueCommands && (
          <GateControlPanel
            gateId={gate.id}
            isOpen={gate.is_open}
            apiBase="customer"
            // The list-level ``gate`` object doesn't include the mqtt
            // summary — that lives on the per-gate detail endpoint
            // (``details``). While details are still loading we
            // optimistically assume connected so the button doesn't
            // flicker disabled-then-enabled on every panel open;
            // the backend's 409 catches the rare case where state
            // changed under us.
            isDeviceOnline={details?.mqtt ? details.mqtt.status === 'online' : true}
            onCommandComplete={() => setCommandsRefreshKey(k => k + 1)}
          />
        )}

        {/* Temp-unlock only makes sense when hermetic lock (wiring 2)
            is actually enabled on this gate. With it off, the gate is
            never auto-locked, so there's nothing to "release" — showing
            the section would just be confusing noise. */}
        {settings?.hermetic_lock_enabled && (
          <GateTempUnlockSection
            gateId={gate.id}
            apiBase="customer"
            canManage={canTempUnlock}
          />
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div style={{ flex: 1, background: '#f7f8fa', borderRadius: '6px', padding: '0.65rem', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{gate.cycle_count.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>סייקלים</div>
          </div>
          <div style={{ flex: 1, background: '#f7f8fa', borderRadius: '6px', padding: '0.65rem', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{gate.model || '-'}</div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>דגם</div>
          </div>
        </div>
      </div>

      {!details && (
        // Skeleton placeholder for the chunk below, which only renders
        // after /api/customer/gates/<id> returns. Without this the modal
        // grew visibly half a second after opening as details landed.
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <SkeletonBox width="32%" height="1.6rem" />
            <SkeletonBox width="32%" height="1.6rem" />
          </div>
          <SkeletonBox width="60%" height="1rem" />
          <SkeletonBox width="100%" height="3.5rem" style={{ marginTop: '0.5rem' }} />
          <SkeletonBox width="100%" height="2.6rem" style={{ marginTop: '0.5rem' }} />
        </div>
      )}

      {details && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {details.fault_history && (
              <FaultHistoryBadge faultHistory={details.fault_history} />
            )}
            <MqttStatusIndicator mqtt={details.mqtt} />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <FaultAcknowledgeButton
              gate={gate}
              apiPrefix="/api/customer"
              onResolved={() => {
                // Refetch the gate detail so the badge collapses
                // back to "X ימים ללא תקלה" immediately, without
                // waiting for the next live-poll tick to refresh.
                axios.get(`/api/customer/gates/${gate.id}`)
                  .then(res => setDetails(res.data));
              }}
            />
          </div>

          <div className="info-row" style={{ fontSize: '0.85rem' }}>
            <span className="info-label">טיפול אחרון</span>
            <span className="info-value">{details.last_maintenance || 'לא ידוע'}</span>
          </div>

          {details.product && (
            <div className="product-summary-card" style={{ marginTop: '0.75rem' }}>
              <div className="product-summary-image">
                {details.product.image_url ? (
                  <img src={details.product.image_url} alt="" />
                ) : (
                  details.product.category_icon_closed_url ? (
                    <img src={details.product.category_icon_closed_url} alt="" />
                  ) : null
                )}
              </div>
              <div className="product-summary-body">
                <div className="product-summary-name">{details.product.name}</div>
                <div className="product-summary-meta">
                  {details.product.category_name && <>{details.product.category_name}</>}
                  {details.product.model_number && <> · {details.product.model_number}</>}
                  {details.product.manufacturer && <> · {details.product.manufacturer}</>}
                </div>
                {details.product.description && <div className="product-summary-desc">{details.product.description}</div>}
              </div>
            </div>
          )}

          <button
            onClick={() => setConfigOpen(true)}
            disabled={settings == null}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 0.85rem',
              border: '1px solid #e5e7eb',
              background: '#fff',
              borderRadius: '6px',
              marginTop: '1.25rem',
              marginBottom: inheritedFromGroup || hasOwnOverride ? '0.4rem' : '1rem',
              cursor: settings == null ? 'wait' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              textAlign: 'right',
              color: '#1a1a2e',
            }}
          >
            <span>הגדרות שער {inheritedFromGroup && <span style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 400, marginRight: '0.4rem' }}>· מהקבוצה</span>}</span>
            <span style={{ color: '#9ca3af' }}>‹</span>
          </button>
          {hasOwnOverride && gate.group_name && (
            <button
              onClick={revertToGroup}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.4rem 0.85rem',
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                color: '#6b7280',
                borderRadius: '5px',
                fontSize: '0.78rem',
                cursor: 'pointer',
                textAlign: 'right',
                marginBottom: '1rem',
              }}
            >
              ↩ השב להגדרות הקבוצה
            </button>
          )}
          {settings && (
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '-0.65rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span>שער פתוח זמן רב: {settings.long_open_alert_enabled ? (settings.long_open_threshold_minutes ? `פעיל (${settings.long_open_threshold_minutes} דק׳)` : 'פעיל') : 'כבוי'}</span>
              <span>תקלה: {settings.fault_alert_enabled ? 'פעיל' : 'כבוי'}</span>
              <span>רכיב לא מחובר: {settings.offline_alert_enabled ? `פעיל (${settings.offline_threshold_minutes} דק׳)` : 'כבוי'}</span>
              <span>סגירה אוטומטית לפי שעה: {settings.auto_close_enabled ? 'פעיל' : 'כבוי'}</span>
              <span>פתיחה אוטומטית לפי שעה: {settings.auto_open_enabled ? 'פעיל' : 'כבוי'}</span>
              <span>נעילה הרמטית לפי חלון שעות: {settings.hermetic_lock_enabled ? 'פעיל' : 'כבוי'}</span>
            </div>
          )}

        </>
      )}
      </>
      )}

      {tab === 'events' && (
        <div className="gate-panel-tab-content">
          <GateTimeline gateId={gate.id} apiBase="customer" />
        </div>
      )}

      {tab === 'commands' && (
        <div className="gate-panel-tab-content">
          <GateCommandsLog gateId={gate.id} apiBase="customer" refreshKey={commandsRefreshKey} />
        </div>
      )}

      {configOpen && settings && (
        <GateConfigMenu
          gateId={gate.id}
          initialSettings={settings}
          onClose={() => setConfigOpen(false)}
          onApplied={() => refreshSettings()}
        />
      )}
    </>
  );
}

// Customer-side "new gate" — intentionally minimal. The customer pins a
// gate with just a name; Huri's technician fills in the model, device,
// and wiring during installation. (The admin modal, by contrast, also
// offers product / device-id / cycle-limit fields.)
function NewGateModal({ siteId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post(`/api/customer/sites/${siteId}/gates`, { name: name.trim() });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'יצירת השער נכשלה');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>שער חדש</h2>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '-0.4rem', marginBottom: '1rem' }}>
          תנו שם לשער ונעצו אותו על המפה. את הדגם והרכיב יתקין טכנאי חורי בהמשך.
        </p>
        <div className="form-group">
          <label>שם השער</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="למשל: שער כניסה ראשי"
            autoFocus
            required
          />
        </div>
        {error && (
          <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{error}</div>
        )}
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'יוצר...' : 'צור שער'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

// Clickable stat card. Re-uses the existing .stat-card classes so the
// look matches every other dashboard, but renders as a button so the
// "click → filter the list" affordance reads as interactive. An active
// outline + box-shadow signals which filter is currently applied.
function StatFilterCard({ label, number, tone, active, onClick }) {
  const className = `stat-card${tone ? ' ' + tone : ''}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-pressed={active}
      style={{
        cursor: 'pointer',
        border: active ? '2px solid #1a1a2e' : '1px solid #e5e7eb',
        boxShadow: active ? '0 2px 6px rgba(26,26,46,0.12)' : undefined,
        font: 'inherit',
        textAlign: 'center',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <div className="number">{number}</div>
      <div className="label">{label}</div>
    </button>
  );
}


export default CustomerSiteMap;
